export type CountryPreferenceSource = "saved" | "ip" | "locale" | "fallback";

/**
 * A few records spell the country out instead of coding it — "France",
 * "USA" — and "UK" is a common stand-in for GB. These read as their codes;
 * anything else that is not two letters is unknown.
 */
const COUNTRY_CODE_ALIASES: Record<string, string> = {
  UK: "GB",
  USA: "US",
  "UNITED STATES": "US",
  "UNITED STATES OF AMERICA": "US",
  CANADA: "CA",
  FRANCE: "FR",
  MEXICO: "MX",
};

/** ISO-3166 alpha-2, upper-cased, or null when the value is not a country. */
export function normalizeCountryCode(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim().toUpperCase();
  if (!trimmed) return null;
  const normalized = COUNTRY_CODE_ALIASES[trimmed] ?? trimmed;
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

/**
 * The order every country guess follows: what is saved, then where the
 * network says she is, then how the device is configured, then the fallback.
 * Each candidate counts only when it is one of the supported codes.
 */
export function resolveCountryPreference({
  savedCountry,
  ipCountry,
  localeCountry,
  supportedCountryCodes,
  fallbackCountry,
}: {
  savedCountry?: string | null;
  ipCountry?: string | null;
  localeCountry?: string | null;
  supportedCountryCodes: readonly string[];
  fallbackCountry: string;
}): { countryCode: string; source: CountryPreferenceSource } {
  const supported = new Set(
    supportedCountryCodes
      .map((value) => normalizeCountryCode(value))
      .filter((value): value is string => value !== null),
  );
  const candidates: {
    value: string | null | undefined;
    source: Exclude<CountryPreferenceSource, "fallback">;
  }[] = [
    { value: savedCountry, source: "saved" },
    { value: ipCountry, source: "ip" },
    { value: localeCountry, source: "locale" },
  ];

  for (const candidate of candidates) {
    const countryCode = normalizeCountryCode(candidate.value);
    if (countryCode && supported.has(countryCode)) {
      return { countryCode, source: candidate.source };
    }
  }

  return {
    countryCode: normalizeCountryCode(fallbackCountry) ?? "US",
    source: "fallback",
  };
}
